import { ContentType } from 'hooks/queries/schedules/useAddToBrandPlanSchedule'
import { DesignChannel } from 'components/NewFileModal/Types'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { AvatarScriptSettings } from 'screens/Calendar/WeekViewV2/BatchContentActions/Modals/NewPostModal/ScriptSettingsModal'
import { isVideoChannel } from 'screens/Calendar/FullScreenPreview/utils'
import { VideoScriptRequestParams } from 'utils/videoScriptRequest'
import type { GenerationDataWithVoiceover } from 'components/Previews/components/VideoPlayer/AudioEditorModal/types'

export interface VideoRegenerationParams {
  video_script?: string
  video_caption_style?: string
  video_voiceover_voice?: string
}

export const isUgcScriptVideoContent = (almanacFile: Pick<AlmanacFileShowItem, 'content_type'>) =>
  almanacFile.content_type === 'video'

const generationVoiceFromPrompt = (prompt: Record<string, unknown> | null | undefined): string | null => {
  const metadata = prompt?.metadata as { generation_data?: GenerationDataWithVoiceover } | undefined
  const voice = metadata?.generation_data?.voiceover?.tts?.voice
  return typeof voice === 'string' && voice ? voice : null
}

/** Voice used for the current generation: explicit override first, then TTS metadata. */
export const resolveVideoVoiceoverVoiceFromPrompt = (
  prompt: Record<string, unknown> | null | undefined,
): string | null => {
  if (typeof prompt?.video_voiceover_voice === 'string' && prompt.video_voiceover_voice) {
    return prompt.video_voiceover_voice
  }
  return generationVoiceFromPrompt(prompt)
}

export const videoScriptSettingsFromPrompt = (
  prompt: Record<string, unknown> | null | undefined,
  fallbackCaptionStyle?: string | null,
): AvatarScriptSettings => ({
  avatar: null,
  avatarAngle: 'testimonial',
  avatarScript: typeof prompt?.video_script === 'string' ? prompt.video_script : '',
  avatarCaptions: typeof prompt?.video_captions === 'boolean' ? prompt.video_captions : true,
  avatarCaptionStyle:
    (typeof prompt?.video_caption_style === 'string' ? prompt.video_caption_style : null) ??
    (typeof prompt?.avatar_caption_style === 'string' ? prompt.avatar_caption_style : null) ??
    fallbackCaptionStyle ??
    null,
  videoVoiceoverVoice: resolveVideoVoiceoverVoiceFromPrompt(prompt),
  productImage: null,
})

export const mergeVideoScriptPrompts = (
  schedulePrompt: Record<string, unknown> | null | undefined,
  generationPrompt: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null | undefined => {
  if (!schedulePrompt && !generationPrompt) return null
  if (!generationPrompt) return schedulePrompt
  if (!schedulePrompt) return generationPrompt

  return {
    ...generationPrompt,
    ...schedulePrompt,
    metadata: generationPrompt.metadata ?? schedulePrompt.metadata,
  }
}

export const videoParamsFromSettings = (settings: AvatarScriptSettings): VideoRegenerationParams => ({
  video_script: settings.avatarScript,
  ...(settings.avatarCaptionStyle ? { video_caption_style: settings.avatarCaptionStyle } : {}),
  ...(settings.videoVoiceoverVoice ? { video_voiceover_voice: settings.videoVoiceoverVoice } : {}),
})

export const resolveVideoScriptApiType = (
  channel: AlmanacFileShowItem['channel'],
  prompt?: Record<string, unknown> | null,
): ContentType | null => {
  if (typeof prompt?.type === 'string') return prompt.type as ContentType
  if (channel && isVideoChannel(channel as DesignChannel)) return 'video_crosspost_post'
  if (channel) return 'crosspost_post'
  return null
}

export const getVideoScriptRequestParamsFromAlmanacFile = (
  almanacFile: AlmanacFileShowItem,
  topic: string,
): VideoScriptRequestParams | null => {
  const brandPlanScheduleId = almanacFile.campaign_info?.id
  const type = resolveVideoScriptApiType(almanacFile.channel, almanacFile.completed_ai_suggestion_request?.prompt)
  if (!brandPlanScheduleId || !type || !topic.trim()) return null

  return {
    brand_plan_schedule_id: brandPlanScheduleId,
    type,
    content_type: 'video',
    topic,
  }
}
